import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  IArnoSelectorField,
  useArnoSelectorCatalog,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { fieldContext, rankFields, technicalHits } from './fieldSearch';

/**
 * Auswahl eines Datenfeldes für den Urkundentext (#1245).
 *
 * Wer eine Urkunde schreibt, sucht nach „Erwerber", „Herrn" oder „Flurstück" — nicht nach
 * `LegalClientArray[index=1].NameMitAnredeGebeugt`. Deshalb steht hier der Klartextname vorn,
 * darunter die Ebene (»Beteiligte › Käufer 2«) und der Wert aus dem Vorschauvorgang; der
 * technische Pfad steht nur unter „Technische Details".
 *
 * **Fachlich, nicht technisch (Teil 47).** Der Katalog kennzeichnet jedes Feld: normal oder nur
 * für Experten (Schlüssel, Doppelungen, Mandantenformular, Altbestand …), jeweils mit Grund.
 * Normal erscheinen nur die fachlichen; „Technische Felder" schaltet die übrigen dazu — und nur
 * dann lässt sich ein Pfad frei eintippen. Die Suche (`fieldSearch.ts`) sucht fachlich:
 * Name, Ebene, Synonyme, Vorschauwert; Pfad und ArnoTop-Namen nur bei den technischen Feldern.
 *
 * Die Liste kommt unverändert aus dem Backend und ist genau das, was der Word-Payload hergibt —
 * es gibt keine zweite, hier gepflegte Feldliste, die davon abweichen könnte.
 */

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Vorgang für die Wertvorschau; ohne ihn bleibt der Katalog datenfrei. */
  idLegalTransaction?: number | null;
  disabled?: boolean;
  label?: string;
}

const TECHNICAL_KEY = 'arno.fieldPicker.technical';

const readTechnical = (): boolean => {
  try {
    return window.localStorage.getItem(TECHNICAL_KEY) === '1';
  } catch {
    return false;
  }
};

const writeTechnical = (on: boolean): void => {
  try {
    window.localStorage.setItem(TECHNICAL_KEY, on ? '1' : '0');
  } catch {
    // Ohne Speicher gilt der Schalter nur bis zum Neuladen.
  }
};

/** Was im Dokument stünde — oder ein ehrlicher Hinweis, dass hier nichts steht. */
const previewText = (field: IArnoSelectorField): string =>
  field.sample && field.sample.trim() ? field.sample : 'Kein Wert vorhanden';

const nameOf = (field: IArnoSelectorField): string =>
  field.displayName ?? field.label;

/** Höchstens so viele Zeilen in der Liste — gesucht wird ohnehin über alle. */
const LIMIT = 150;

export const SelectorPicker: React.FC<Props> = ({
  value,
  onChange,
  idLegalTransaction,
  disabled,
  label = 'Datenfeld',
}) => {
  const { selectorCatalog, isLoadingSelectorCatalog } =
    useArnoSelectorCatalog(idLegalTransaction);
  const [showDetails, setShowDetails] = useState(false);
  const [technical, setTechnical] = useState(readTechnical);
  const [query, setQuery] = useState('');

  const options = useMemo(
    () =>
      (selectorCatalog?.groups ?? []).flatMap(group =>
        group.fields.map(field => ({ ...field, group: group.label }))
      ),
    [selectorCatalog]
  );

  const known = useMemo(
    () => options.find(option => option.path === value) ?? null,
    [options, value]
  );
  // Ein Pfad, den der Katalog nicht kennt (von Hand eingetragen, anderes Datenmodell), bleibt
  // sichtbar — als das, was er ist.
  const selected = useMemo<IArnoSelectorField | null>(
    () =>
      known ??
      (value
        ? {
            path: value,
            label: value,
            displayName: value,
            group: 'Unbekanntes Feld',
            visibility: 'expert',
            reason: 'Dieses Feld kennt der Feldkatalog nicht.',
            arno: [],
            sample: null,
          }
        : null),
    [known, value]
  );
  const choices = useMemo(
    () => (selected && !known ? [...options, selected] : options),
    [options, selected, known]
  );

  const searching =
    query.trim().length > 0 && query !== (selected ? nameOf(selected) : '');
  const noResults = (() => {
    if (!searching || technical) {
      return 'Kein Feld gefunden';
    }
    const hits = technicalHits(options, query);
    return hits > 0
      ? `Kein fachliches Feld gefunden. ${hits === 1 ? 'Ein technisches Feld passt' : `${hits} technische Felder passen`} — „Technische Felder" einschalten.`
      : 'Kein Feld gefunden';
  })();

  const toggleTechnical = (on: boolean) => {
    setTechnical(on);
    writeTechnical(on);
  };

  return (
    <Stack spacing={0.5}>
      <Autocomplete<IArnoSelectorField, false, false, boolean>
        freeSolo={technical}
        options={choices}
        filterOptions={(list, state) =>
          rankFields(list, state.inputValue, { technical, limit: LIMIT })
        }
        groupBy={searching ? undefined : option => fieldContext(option)}
        value={selected}
        disabled={disabled}
        loading={isLoadingSelectorCatalog}
        noOptionsText={noResults}
        isOptionEqualToValue={(option, current) =>
          option.path === (typeof current === 'string' ? current : current.path)
        }
        getOptionLabel={option =>
          typeof option === 'string' ? option : nameOf(option)
        }
        onChange={(_event, next) =>
          onChange(typeof next === 'string' ? next : (next?.path ?? ''))
        }
        onInputChange={(_event, next, reason) => {
          setQuery(next);
          // Einen Pfad frei eintippen: nur mit den technischen Feldern.
          if (technical && reason === 'input') {
            onChange(next);
          }
        }}
        renderOption={(props, option) => (
          <Box component='li' {...props} key={option.path}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='body2'>{option.label}</Typography>
              {searching && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block' }}
                  noWrap
                >
                  {fieldContext(option)}
                </Typography>
              )}
              <Typography
                variant='caption'
                color={option.sample ? 'text.primary' : 'text.disabled'}
                sx={{ display: 'block' }}
                noWrap
              >
                {previewText(option)}
              </Typography>
              {option.visibility === 'expert' && (
                <Typography
                  variant='caption'
                  color='warning.main'
                  sx={{ display: 'block' }}
                >
                  Technisches Feld · {option.reason}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={parameters => (
          <TextField
            {...parameters}
            size='small'
            label={label}
            placeholder='z. B. Erwerber, Herrn, Flurstück, Urkundennummer'
            helperText={
              isLoadingSelectorCatalog
                ? 'Datenfelder werden geladen …'
                : idLegalTransaction
                  ? 'Vorschauwerte stammen aus dem gewählten Vorgang.'
                  : 'Ohne Vorgang gibt es keine Vorschauwerte.'
            }
          />
        )}
      />

      {selected && (
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Chip
            size='small'
            variant='outlined'
            label={previewText(selected)}
            color={selected.sample ? 'default' : 'warning'}
          />
          {selected.kind === 'aggregate' && (
            <Chip size='small' variant='outlined' label='Sammelfeld' />
          )}
          {selected.visibility === 'expert' && (
            <Chip
              size='small'
              color='warning'
              variant='outlined'
              label='Technisches Feld'
            />
          )}
          <Link
            component='button'
            type='button'
            variant='caption'
            underline='hover'
            onClick={() => setShowDetails(current => !current)}
          >
            {showDetails
              ? 'Technische Details ausblenden'
              : 'ⓘ Technische Details'}
          </Link>
        </Stack>
      )}
      {selected?.description && (
        <Typography variant='caption' color='text.secondary'>
          {selected.description}
        </Typography>
      )}

      <Collapse in={showDetails && Boolean(selected)} unmountOnExit>
        <Typography
          variant='caption'
          color='text.secondary'
          component='div'
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {selected?.path}
          {selected?.arno?.length
            ? ` · ArnoTop: ${selected.arno.join(', ')}`
            : ''}
        </Typography>
        {selected?.reason && (
          <Typography variant='caption' color='text.secondary' component='div'>
            {selected.reason}
          </Typography>
        )}
      </Collapse>

      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Switch
            size='small'
            checked={technical}
            onChange={event => toggleTechnical(event.target.checked)}
          />
        }
        label={
          <Typography variant='caption' color='text.secondary'>
            Technische Felder
          </Typography>
        }
      />
    </Stack>
  );
};
